import { getMap } from './mapInstance.js';
import * as olProj from 'ol/proj.js';
import { Style, Circle as CircleStyle, Fill, Stroke, Text } from 'ol/style.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import { getSelectedCountryAbbreviation } from './country-language.js';
import * as MapUtils from './mapUtils.js';
import { API_URL } from './config.js';
import { secureFetch } from './securityUtil.js';
import { resolveSearchCountry, runSuggestionProviders } from './autocompleteRequests.js';

// ---------------------------
// Marker Layer Setup
// ---------------------------
const locationMarkerSource = new VectorSource();
const searchPoiLayersByCategory = new Map();
const SEARCH_POI_ICON = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32"%3E%3Cpath fill="%23e11d48" stroke="white" stroke-width="2" d="M12 1C5.9 1 1 5.9 1 12c0 8.2 11 18.5 11 18.5S23 20.2 23 12C23 5.9 18.1 1 12 1z"/%3E%3Ccircle cx="12" cy="12" r="4" fill="white"/%3E%3C/svg%3E';
const createLocationMarkerStyle = feature => new Style({
  image: new CircleStyle({
    radius: 7,
    fill: new Fill({ color: 'red' }),
    stroke: new Stroke({ color: 'white', width: 2 })
  }),
  text: new Text({
    text: String(feature.get('label') || ''),
    offsetY: -18,
    font: '600 12px Inter, system-ui, sans-serif',
    fill: new Fill({ color: '#111827' }),
    stroke: new Stroke({ color: '#ffffff', width: 3 }),
    overflow: true
  })
});
const locationMarkerLayer = new VectorLayer({
  source: locationMarkerSource,
  style: createLocationMarkerStyle
});
locationMarkerLayer.setZIndex(20001);
locationMarkerLayer.set('searchLocationMarker', true);
locationMarkerLayer.set('title', 'Search Location');

let map = null;
let selectedAreaResult = null;

export function initSearchByLocationLayer() {
  map = getMap();
  if (!map) return;

  if (!map.getLayers().getArray().includes(locationMarkerLayer)) {
    map.addLayer(locationMarkerLayer);
  }
}

function ensureSearchPoiLayer(category) {
  const layers = window.own_map?.getLayers?.();
  if (!layers) return null;
  const categoryName = String(category || 'location').trim() || 'location';
  const categoryKey = categoryName.toLowerCase();
  let layer = searchPoiLayersByCategory.get(categoryKey);
  if (!layer) {
    layer = new VectorLayer({
      source: new VectorSource(),
      style: createLocationMarkerStyle,
      visible: true,
      zIndex: 20001
    });
    layer.set('title', categoryName);
    layer.set('group', 'ownData');
    layer.set('poiCategory', categoryName);
    layer.set('searchPoiCategory', true);
    layer.set('poiStyleColor', '#e11d48');
    layer.set('poiLegendIconSrc', SEARCH_POI_ICON);
    searchPoiLayersByCategory.set(categoryKey, layer);
  }
  if (!layers.getArray().includes(layer)) layers.push(layer);
  window.own_map.setVisible(true);
  return layer;
}

// ---------------------------
// Country Bounding Boxes
// [minLon, minLat, maxLon, maxLat]
// ---------------------------
const COUNTRY_BBOX = {
  in: [68.7, 6.5, 97.25, 35.5],   // India
  nl: [3.3, 50.7, 7.2, 53.6],    // Netherlands
  de: [5.8, 47.2, 15.1, 55.1],   // Germany
  be: [2.5, 49.5, 6.4, 51.6]     // Belgium
};

// ---------------------------
// Helper: Hard bbox check
// ---------------------------
function isInsideBbox(lon, lat, bbox) {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  return (
    lon >= minLon &&
    lon <= maxLon &&
    lat >= minLat &&
    lat <= maxLat
  );
}

// ---------------------------
// Geocode with STRICT country filtering
// ---------------------------
async function geocodeLocationAutocomplete(query, signal) {
  const countryCode = resolveSearchCountry(getSelectedCountryAbbreviation(), document.getElementById('country-select')?.value);
  //console.log('geocodeAutocomplete country:', countryCode);

  const bbox = COUNTRY_BBOX[countryCode];

  let url =
    `https://nominatim.openstreetmap.org/search` +
    `?format=json` +
    `&addressdetails=1` +
    `&limit=10` +
    `&q=${encodeURIComponent(query)}`;

  if (countryCode) {
    url += `&countrycodes=${countryCode}`;
  }

  if (bbox) {
    // bias only — DO NOT bound
    url += `&viewbox=${bbox[0]},${bbox[3]},${bbox[2]},${bbox[1]}`;
  }

  const resp = await fetch(url, {
    signal,
    headers: {
      'Accept-Language': 'en'
    }
  });

  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

  const data = await resp.json();

  return data.filter(r => {
    if (!r.address || !r.address.country_code) return false;

    if (countryCode && r.address.country_code.toLowerCase() !== countryCode) return false;

    if (!bbox) return true;

    const lon = parseFloat(r.lon);
    const lat = parseFloat(r.lat);

    // small tolerance for NL borders
    const padding = 0.2;
    return isInsideBbox(lon, lat, [
      bbox[0] - padding,
      bbox[1] - padding,
      bbox[2] + padding,
      bbox[3] + padding
    ]);
  }).slice(0, 10);
}

function normalizeSearchResult(result) {
  const coordinates = result?.geometry?.coordinates ?? result?.location?.coordinates;
  const lon = result?.lon ?? result?.lng ?? result?.long ?? result?.longitude ?? result?.center?.lng ?? result?.geometry?.location?.lng ?? coordinates?.[0];
  const lat = result?.lat ?? result?.latitude ?? result?.center?.lat ?? result?.geometry?.location?.lat ?? coordinates?.[1];
  const displayName = result?.display_name ?? result?.displayName ?? result?.formatted_address ?? result?.full_address ?? result?.label ?? result?.name;
  if (!displayName || !Number.isFinite(Number(lon)) || !Number.isFinite(Number(lat))) return null;
  return { ...result, display_name: String(displayName), lon: Number(lon), lat: Number(lat) };
}

async function geocodeAreaAutocomplete(query, signal, companiesOnly = false) {
  const params = new URLSearchParams({
    search_query: query,
    companies: companiesOnly ? '1' : '0'
  });
  // Existing /geo/search contract: an empty supported-type subset skips the
  // area queries. Company search can then run independently of area search.
  if (companiesOnly) params.set('types', 'none');
  const country = resolveSearchCountry(getSelectedCountryAbbreviation(), document.getElementById('country-select')?.value);
  if (country) params.set('country_code', country.toUpperCase());
  const response = await secureFetch(`${API_URL}geo/search?${params.toString()}`, {
    signal,
    headers: { Accept: 'application/json' }
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = await response.json();
  const rows = Array.isArray(payload) ? payload
    : Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload?.results) ? payload.results
        : Array.isArray(payload?.data?.results) ? payload.data.results : [];
  return rows.map(normalizeSearchResult).filter(Boolean).slice(0, 10);
}

async function geocodeCombinedAutocomplete(query, options = {}) {
  return runSuggestionProviders([
    async signal => (await geocodeLocationAutocomplete(query, signal))
      .map(result => ({ ...result, searchResultType: 'location' })),
    async signal => (await geocodeAreaAutocomplete(query, signal))
      .map(result => ({ ...result, searchResultType: 'area' })),
    async signal => (await geocodeAreaAutocomplete(query, signal, true))
      .map(result => ({ ...result, searchResultType: 'area' }))
  ], options);
}

function getCompanyDetailRow(payload) {
  const feature = payload?.features?.[0] ?? payload?.data?.features?.[0];
  if (feature) return { ...(feature.properties || {}), geometry: feature.geometry };
  const candidate = payload?.data?.result ?? payload?.data?.company ?? payload?.data
    ?? payload?.result ?? payload?.company ?? payload;
  if (Array.isArray(candidate)) return candidate[0] || null;
  return candidate && typeof candidate === 'object' ? candidate : null;
}

async function resolveCompanySearchResult(result) {
  if (String(result?.type || '').toLowerCase() !== 'company') return result;
  const placeId = result?.place_id;
  if (placeId === undefined || placeId === null || String(placeId).trim() === '') {
    throw new Error('The selected company has no place_id');
  }

  const response = await secureFetch(`${API_URL}geo/companies/${encodeURIComponent(String(placeId))}`, {
    headers: { Accept: 'application/json' }
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || `HTTP ${response.status}`);
  const detail = getCompanyDetailRow(payload);
  const normalized = normalizeSearchResult({ ...result, ...(detail || {}) });
  if (!normalized) throw new Error('The company response contains no valid location');
  return { ...normalized, searchResultType: result.searchResultType, type: result.type, place_id: placeId };
}

// ---------------------------
// Search History
// ---------------------------
let searchHistory = [];

function addToHistory(result) {
  if (!searchHistory.find(h => h.display_name === result.display_name)) {
    searchHistory.unshift(result);
    if (searchHistory.length > 5) searchHistory.pop();
  }
}

function addSelectedLocationMarker(result) {
  if (!map) map = getMap();
  if (!map || !result) return false;
  const lon = Number(result.lon);
  const lat = Number(result.lat);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
  const coord = map.getView().getProjection().getCode() === 'EPSG:4326'
    ? [lon, lat]
    : olProj.fromLonLat([lon, lat]);
  const marker = new Feature(new Point(coord));
  marker.set('label', result.display_name || result.label || result.name || 'Selected location');
  marker.set('type', String(result.type || 'location').trim().toLowerCase());
  marker.set('place_id', result.place_id ?? null);
  locationMarkerSource.clear();
  locationMarkerSource.addFeature(marker);
  return true;
}

function addLocationToPointOfInterests(result) {
  if (!map) map = getMap();
  if (!map || !result) return false;
  const category = String(result.type || 'location').trim() || 'location';
  const poiLayer = ensureSearchPoiLayer(category);
  if (!poiLayer) return false;
  const poiSource = poiLayer.getSource();
  const lon = Number(result.lon);
  const lat = Number(result.lat);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
  const coord = map.getView().getProjection().getCode() === 'EPSG:4326'
    ? [lon, lat]
    : olProj.fromLonLat([lon, lat]);
  const key = String(result.place_id ?? result.id ?? `${lon},${lat}`);
  let feature = poiSource.getFeatureById(key);
  if (!feature) {
    feature = new Feature(new Point(coord));
    feature.setId(key);
    poiSource.addFeature(feature);
  } else {
    feature.getGeometry()?.setCoordinates(coord);
  }
  feature.setProperties({
    label: result.display_name || result.label || result.name || 'Selected location',
    name: result.display_name || result.label || result.name || 'Selected location',
    type: category,
    category,
    place_id: result.place_id ?? null
  });
  document.dispatchEvent(new CustomEvent('poi-layers-updated'));
  return true;
}

// ---------------------------
// Show Results Dropdown
// ---------------------------
function showSearchMessage(resultsContainer, message) {
  resultsContainer.innerHTML = '';
  const item = document.createElement('li');
  item.className = 'list-group-item list-group-item-sm search-result-message';
  item.setAttribute('role', 'status');
  item.textContent = message;
  resultsContainer.appendChild(item);
  resultsContainer.style.display = 'block';
}

function showResults(results, resultsContainer, input) {
  resultsContainer.innerHTML = '';

  if (!results.length) {
    showSearchMessage(resultsContainer, 'No results found ...!');
    return;
  }

  results.forEach(r => {
    const li = document.createElement('li');
    li.className = 'list-group-item list-group-item-action list-group-item-sm';
    const label = document.createElement('span');
    label.className = 'search-result-label';
    label.textContent = r.display_name;
    const type = document.createElement('span');
    type.className = `search-result-type is-${r.searchResultType || 'location'}`;
    type.textContent = r.searchResultType === 'area'
      ? String(r.type || 'Area')
      : String(r.addresstype || r.type || 'Location');
    li.append(label, type);
    li.addEventListener('click', async event => {
      event.preventDefault();
      event.stopPropagation();
      try {
        const selectedResult = await resolveCompanySearchResult(r);
        input.value = selectedResult.display_name;
        moveToResult(selectedResult, resultsContainer);
      } catch (error) {
        console.error('Company location search failed:', error);
        MapUtils.setStatus(error.message || 'Unable to load the selected company location');
      }
    });
    resultsContainer.appendChild(li);
  });

  resultsContainer.style.display = 'block';
}

// ---------------------------
// Move to Result on Map
// ---------------------------
function moveToResult(r, resultsContainer) {
  const isAreaResult = r.searchResultType === 'area';
  const command = isAreaResult ? 'Search by Area' : 'Search by Location';
  if (!map) map = getMap();
  if (!map) {
    MapUtils.setStatus('Map is not ready. Please try again.');
    return;
  }
  const lon = parseFloat(r.lon);
  const lat = parseFloat(r.lat);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    MapUtils.setStatus('The selected result has no valid map coordinates');
    return;
  }
  const requestedZoom = Number(r.zoom);
  const zoom = Number.isFinite(requestedZoom)
    ? Math.min(20, Math.max(2, requestedZoom))
    : 16;

  const coord =
    map.getView().getProjection().getCode() === 'EPSG:4326'
      ? [lon, lat]
      : olProj.fromLonLat([lon, lat]);

  addSelectedLocationMarker(r);

  const bbox = Array.isArray(r.bbox) ? r.bbox.map(Number) : null;
  const hasValidBbox = bbox?.length === 4
    && bbox.every(Number.isFinite)
    && bbox[0] < bbox[2]
    && bbox[1] < bbox[3];

  if (hasValidBbox) {
    const projection = map.getView().getProjection();
    const extent = projection.getCode() === 'EPSG:4326'
      ? bbox
      : olProj.transformExtent(bbox, 'EPSG:4326', projection);
    map.getView().fit(extent, {
      padding: [40, 40, 40, 40],
      duration: 600,
      maxZoom: zoom
    });
  } else {
    map.getView().animate({ center: coord, zoom, duration: 600 });
  }

  MapUtils.setCommand(command);
  MapUtils.setStatus(`Moved to: ${r.display_name}`);

  addToHistory(r);
  resultsContainer.style.display = 'none';

  if (isAreaResult) {
    selectedAreaResult = r;
    const addButton = document.getElementById('search-area-add');
    if (addButton) {
      addButton.disabled = false;
      addButton.title = `Add ${r.display_name}`;
      addButton.setAttribute('aria-label', `Add ${r.display_name}`);
    }
  } else {
    selectedAreaResult = null;
    const addButton = document.getElementById('search-area-add');
    if (addButton) {
      addButton.disabled = true;
      addButton.title = 'Select an area result first';
      addButton.setAttribute('aria-label', 'Select an area result first');
    }
  }
}

function getPostcodeRows(payload) {
  if (payload?.code !== undefined && payload?.code !== null) return [payload.code];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  if (Array.isArray(payload?.postcodes)) return payload.postcodes;
  if (Array.isArray(payload?.data?.postcodes)) return payload.data.postcodes;
  if (Array.isArray(payload?.codes)) return payload.codes;
  if (Array.isArray(payload?.data?.codes)) return payload.data.codes;
  return [];
}

async function addSelectedAreaPostcodes(button) {
  if (!selectedAreaResult || button.disabled) return;
  const areaResult = selectedAreaResult;
  addSelectedLocationMarker(areaResult);
  const isCompany = String(areaResult.type || '').toLowerCase() === 'company';
  const selectedId = areaResult.id ?? areaResult.place_id;
  if (!isCompany && !selectedId) {
    MapUtils.setStatus('The selected area has no place identifier');
    return;
  }

  button.disabled = true;
  button.classList.add('is-loading');
  try {
    const params = new URLSearchParams({ type: 'pc4' });
    if (isCompany) {
      const lat = Number(areaResult.lat);
      const lng = Number(areaResult.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('The selected company has no valid location');
      params.set('lat', String(lat));
      params.set('lng', String(lng));
    } else {
      params.set('id', String(selectedId));
    }
    const endpoint = isCompany ? 'geo/postcode-at' : 'geo/postcodes';
    const response = await secureFetch(`${API_URL}${endpoint}?${params.toString()}`, {
      headers: { Accept: 'application/json' }
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const details = Object.values(payload?.errors || {}).flat().join(' ');
      throw new Error(details || payload?.message || `HTTP ${response.status}`);
    }

    const rows = getPostcodeRows(payload);
    if (!rows.length) throw new Error('No postcode results were returned for this area');
    const postcodes = rows
      .map(row => typeof row === 'string' || typeof row === 'number' ? row : row?.id ?? row?.postcode ?? row?.postal_code ?? row?.code)
      .filter(value => value !== undefined && value !== null && String(value).trim());
    if (!postcodes.length) throw new Error('The postcode response contains no postcode identifiers');

    const { selectPCFeaturesByPostcodes } = await import('./pcSelection.js');
    const country = String(getSelectedCountryAbbreviation() || '').toLowerCase();
    const selectionLevel = ['be', 'bel'].includes(country) ? 'POSTALCODE' : 'PC4';
    const selectedFeatures = await selectPCFeaturesByPostcodes(selectionLevel, postcodes, {
      clearExisting: false,
      fitToResults: true,
      resultRows: rows
    });
    if (!selectedFeatures.length) {
      throw new Error('No matching postcode map features were found for the returned postcodes');
    }
    MapUtils.setCommand('Add Area Search Result');
    MapUtils.setStatus(`${selectedFeatures.length} postcode area(s) selected for ${areaResult.display_name}`);
    if (selectedAreaResult === areaResult) clearSearchLocationSelection({ focus: false });
  } catch (error) {
    console.error('Area postcode search failed:', error);
    MapUtils.setStatus(error.message || 'Unable to load postcodes for the selected area');
  } finally {
    button.classList.remove('is-loading');
    button.disabled = !selectedAreaResult;
  }
}

// ---------------------------
// Setup Search Bar
// ---------------------------
function setupAutocomplete(input, resultsContainer, geocoder) {
  if (!input || !resultsContainer || input.dataset.searchInitialized === 'true') return;
  input.dataset.searchInitialized = 'true';

  let timeout;
  let requestSequence = 0;
  let activeRequest;

  input.addEventListener('input', () => {
    const val = input.value.trim();
    clearTimeout(timeout);
    activeRequest?.abort();
    const sequence = ++requestSequence;
    resultsContainer.style.display = 'none';
    resultsContainer.innerHTML = '';
    document.getElementById('search-location-clear')?.classList.toggle('is-visible', val.length > 0);

    selectedAreaResult = null;
    const addButton = document.getElementById('search-area-add');
    if (addButton) {
      addButton.disabled = true;
      addButton.title = 'Select an area result first';
      addButton.setAttribute('aria-label', 'Select an area result first');
    }

    if (val.length < 3) {
      resultsContainer.style.display = 'none';
      resultsContainer.innerHTML = '';
      locationMarkerSource.clear();
      return;
    }

    timeout = setTimeout(async () => {
      const request = new AbortController();
      activeRequest = request;
      const isCurrent = () => sequence === requestSequence && input.value.trim() === val;
      showSearchMessage(resultsContainer, 'Searching...');
      try {
        const results = await geocoder(val, {
          signal: request.signal,
          onResults: results => { if (isCurrent()) showResults(results, resultsContainer, input); }
        });
        if (sequence !== requestSequence || input.value.trim() !== val) return;
        showResults(results, resultsContainer, input);
      } catch (err) {
        if (sequence !== requestSequence || input.value.trim() !== val) return;
        console.error('Search error:', err);
        showSearchMessage(resultsContainer, 'Unable to search. Please try again.');
      }
    }, 300);
  });

  input.addEventListener('keydown', e => {
    if (e.key === 'Enter' && resultsContainer.style.display !== 'none') {
      const firstResult = resultsContainer.querySelector('.list-group-item-action');
      if (firstResult) {
        e.preventDefault();
        firstResult.click();
      }
    }
  });

}

function clearSearchLocationSelection({ focus = true } = {}) {
  const searchInput = document.getElementById('search-location-text');
  const searchResults = document.getElementById('search-location-results');
  const clearButton = document.getElementById('search-location-clear');
  const addButton = document.getElementById('search-area-add');

  selectedAreaResult = null;
  locationMarkerSource.clear();
  if (searchResults) {
    searchResults.innerHTML = '';
    searchResults.style.display = 'none';
  }
  if (addButton) {
    addButton.disabled = true;
    addButton.title = 'Select an area result first';
    addButton.setAttribute('aria-label', 'Select an area result first');
  }
  clearButton?.classList.remove('is-visible');
  if (searchInput) {
    searchInput.value = '';
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    if (focus) searchInput.focus();
  }
}

// ---------------------------
// Setup combined Location (OSM) and Area (GIS API) search bar
// ---------------------------
export function setupSearchByLocation() {
  const searchInput = document.getElementById('search-location-text');
  const searchResults = document.getElementById('search-location-results');
  setupAutocomplete(
    searchInput,
    searchResults,
    geocodeCombinedAutocomplete
  );

  const clearButton = document.getElementById('search-location-clear');
  clearButton?.classList.toggle('is-visible', Boolean(searchInput?.value.trim()));
  if (clearButton && clearButton.dataset.searchInitialized !== 'true') {
    clearButton.dataset.searchInitialized = 'true';
    const clearSelection = event => {
      event.preventDefault();
      event.stopPropagation();
      clearSearchLocationSelection();
    };
    clearButton.addEventListener('pointerdown', clearSelection);
    clearButton.addEventListener('click', clearSelection);
  }

  const radiusInput = document.getElementById('search-radius-km');
  if (radiusInput && radiusInput.dataset.searchInitialized !== 'true') {
    radiusInput.dataset.searchInitialized = 'true';
    radiusInput.addEventListener('focus', () => {
      if (Number(radiusInput.value) === 0) radiusInput.value = '';
    });
    radiusInput.addEventListener('keydown', event => {
      if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) event.preventDefault();
    });
    radiusInput.addEventListener('input', () => {
      if (!radiusInput.value || /^\d+$/.test(radiusInput.value)) return;
      const value = Number(radiusInput.value);
      radiusInput.value = Number.isFinite(value) ? String(Math.max(0, Math.trunc(value))) : '';
    });
    radiusInput.addEventListener('blur', () => {
      if (radiusInput.value === '') radiusInput.value = '0';
    });
  }

  const areaAddButton = document.getElementById('search-area-add');
  if (areaAddButton && areaAddButton.dataset.searchInitialized !== 'true') {
    areaAddButton.dataset.searchInitialized = 'true';
    areaAddButton.addEventListener('click', () => addSelectedAreaPostcodes(areaAddButton));
  }
  if (document.body.dataset.searchDismissInitialized !== 'true') {
    document.body.dataset.searchDismissInitialized = 'true';
    document.addEventListener('click', e => {
      if (!document.getElementById('search-tools')?.contains(e.target)) {
        document.getElementById('search-location-results')?.style.setProperty('display', 'none');
      }
    });
  }
}
